import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  ButtonBase,
  Tabs,
  Tab,
  Divider
} from "@material-ui/core";
import {
  Loader,
  Sheet,
  SheetContent,
  LogoIconGGT,
  LogoVaxYes,
  Covid19VaccineCard,
  Covid19TestCard,
  Covid19VaccineCertificate
} from "../../components";
import { useHeader } from "../../contexts";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/react-hooks";
import {
  POST_GET_APPOINTMENTS_BY_PHONE,
  POST_LOOKUP_CERTIFICATE
} from "../../queries";
import { useConsumerAuth } from "../../hooks";
import { Trans, useTranslation } from "react-i18next";
import { ROUTE_PATHS } from "../../constants";
import { makeStyles } from "@material-ui/styles";
import { isTestService, isVaxService } from "../../utils";

const useClasses = makeStyles(theme => ({
  navButtonsContainer: {
    backgroundColor: theme.palette.common.white
  },
  navButtonsButton: {
    display: "initial",
    textAlign: "center"
  }
}));

const TABS = [
  {
    id: "tab_appointments",
    label_translation_key: "dashboard.appointments_title"
  },
  {
    id: "tab_certificates",
    label_translation_key: "dashboard.certificates_title"
  }
];

const Dashboard = () => {
  const { t } = useTranslation();
  const { setTitle } = useHeader();
  const history = useHistory();
  const {
    dob,
    phone_number,
    session_token,
    first_name,
    last_name
  } = useConsumerAuth();
  const classes = useClasses();

  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [appointments, setAppointments] = useState();
  const [certificates, setCertificates] = useState();
  const [noCertificateReason, setNoCertificateReason] = useState(
    "You have no certificates."
  );

  useEffect(() => {
    setTitle(t("dashboard.title"));
  }, []);

  const group_code = sessionStorage.getItem("Group_Code");

  /**
   * Set default tab
   */
  useEffect(() => {
    if (group_code) 
      setActiveTab(TABS[1].id);

    if (!appointments || !certificates) return;

    if (appointments.length === 0 && certificates.length > 0)
      setActiveTab(TABS[1].id);

  }, [appointments, certificates]);

  /**
   * Lookup certificates
   */
  const [getCertificates, { loading: loadingCertificates }] = useMutation(
    POST_LOOKUP_CERTIFICATE,
    {
      variables: {
        body: {
          first_name,
          last_name,
          token: session_token,
          phone_number,
          dob
        }
      },
      onCompleted: ({ certificate }) => {
        if (!certificate) return;

        if (
          certificate.results &&
          Array.isArray(certificate.results.certificates)
        ) {
          setCertificates(certificate.results.certificates);
        } else if (certificate.reason_code) {
          setNoCertificateReason(certificate.reason_code);
        }
      }
    }
  );

  /**
   * Lookup appointments by phone number
   */
  const [
    getAppointmentsByPhone,
    { loading: loadingAppointments, error }
  ] = useMutation(POST_GET_APPOINTMENTS_BY_PHONE, {
    variables: {
      body: {
        first_name,
        last_name,
        token: session_token,
        phone_number,
        dob
      }
    },
    onCompleted: ({ appointments }) => {
      if (
        appointments &&
        appointments.results &&
        Array.isArray(appointments.results)
      ) {
        // Filter out "pending" Test appointments
        const filteredAppointments = appointments.results.filter(appointment =>
          appointment.appointment_status
            ? appointment.appointment_status !== "pending"
            : true
        );

        setAppointments(filteredAppointments);
      }
    }
  });

  /**
   * If patient doesn't have a session token, don't lookup appointments/certs
   */
  useEffect(() => {
    if (!session_token) return;

    getAppointmentsByPhone();
    getCertificates();
  }, []);

  /**
   * Handle schedule test
   */
  const handleScheduleTest = () =>
    history.push({ pathname: ROUTE_PATHS.GGT.SCHEDULE_START });

  /**
   * Handle schedule vaccination
   */
  const handleGetVaxYesCard = () =>
    history.push({ pathname: ROUTE_PATHS.VAXYES.REGISTER_BASE });

  if (loadingAppointments || loadingCertificates) return <Loader />;
  if (error)
    return <Typography color="error">{t("dashboard.error_api")}</Typography>;

  return (
    <Box data-cy="dashboard-view">
      <Box className={classes.navButtonsContainer}>
        <Grid container justify="center">
          <Grid item xs={4} md={2}>
            <Box py={2} px={1} textAlign="center">
              <ButtonBase
                onClick={() => handleScheduleTest()}
                className={classes.navButtonsButton}
              >
                <Box mb={1}>
                  <LogoIconGGT />
                </Box>
                <Typography component="div">
                  <Trans i18nKey="dashboard.button_new_test">
                    <Box>Get</Box>
                    <Box>Covid-19 Tested</Box>
                  </Trans>
                </Typography>
              </ButtonBase>
            </Box>
          </Grid>
          <Grid item xs={4} md={2}>
            <Box py={2} px={1} textAlign="center">
              <ButtonBase
                onClick={() => handleGetVaxYesCard()}
                className={classes.navButtonsButton}
              >
                <Box mb={1}>
                  <LogoVaxYes />
                </Box>
                <Typography component="div">
                  <Trans i18nKey="dashboard.button_new_vax">
                    <Box>Get</Box>
                    <Box>Vaccine Card</Box>
                  </Trans>
                </Typography>
              </ButtonBase>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Tabs
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        indicatorColor="primary"
        centered
      >
        {TABS.map(tab => (
          <Tab
            key={`tab-${tab.id}`}
            value={tab.id}
            label={t(tab.label_translation_key)}
          />
        ))}
      </Tabs>
      <Divider />
      <Sheet>
        <SheetContent>
          {activeTab === "tab_appointments" && (
            <>
              {appointments && appointments.length > 0 ? (
                <Box data-cy="dashboard-appointments-list">
                  {appointments.map((appointment, index) => (
                    <Box mb={2} key={`appointment-${index}`}>
                      {isVaxService([appointment.service]) && (
                        <Covid19VaccineCard appointment={appointment} />
                      )}
                      {isTestService([appointment.service]) && (
                        <Covid19TestCard appointment={appointment} />
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  data-cy="dashboard-appointments-list-empty"
                  paragraph
                >
                  {t("dashboard.error_empty")}
                </Typography>
              )}
            </>
          )}
          {activeTab === "tab_certificates" && (
            <>
              {certificates && certificates.length > 0 ? (
                <Covid19VaccineCertificate />
              ) : (
                <Typography paragraph>{noCertificateReason}</Typography>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </Box>
  );
};

export { Dashboard };
